import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Loading } from 'tdesign-react';
import { fetchPortalProfile } from '@/services/portal';
import { probeCurrentUser } from '@/services/user';
import { clearPortalAuth, getPortalToken } from '@/utils/auth';
import { usePortalAccount } from '@/contexts/PortalAccountContext';

interface Props {
  children: ReactNode;
}

type GuardState = 'pending' | 'portal' | 'admin' | 'guest';

const ADMIN_PLACEHOLDER_IDS = new Set(['', 'local-dev']);

/**
 * 门户路由守卫
 *
 * 准入逻辑：
 * 1. 本地有 portal token → 调 /portal/me 验证；通过则放行（"portal"）。
 * 2. 无 portal token → 探测太湖身份；命中合法 userId 则放行（"admin"，后台用户也能浏览门户）。
 * 3. 都不命中 → 跳 /portal/login。
 *
 * 校验失败的 401/403 由 axios 拦截器统一兜底跳转，这里只负责加载态与放行判定。
 *
 * 账号信息（name / organization ...）**只**写入 Provider 内存态，不再持久化，
 * 用户无法通过改 localStorage 伪造身份影响前端逻辑。
 */
const PortalGuard = ({ children }: Props) => {
  const [state, setState] = useState<GuardState>('pending');
  const { account, setAccount } = usePortalAccount();

  useEffect(() => {
    let mounted = true;

    const probe = async () => {
      // 1) 优先按门户 token 验证
      const portalToken = getPortalToken();
      if (portalToken) {
        try {
          const acc = await fetchPortalProfile();
          if (!mounted) return;
          setAccount(acc);
          setState('portal');
          return;
        } catch {
          if (!mounted) return;
          clearPortalAuth();
          setAccount(null);
          // 拦截器通常已经 replace 走了；继续探测太湖兜底
        }
      }

      // 2) 后台太湖用户也允许访问门户
      try {
        const u = await probeCurrentUser();
        if (!mounted) return;
        if (u && !ADMIN_PLACEHOLDER_IDS.has(u.userId)) {
          setState('admin');
          return;
        }
      } catch {
        /* 静默：未登录 */
      }

      if (mounted) setState('guest');
    };

    void probe();
    return () => {
      mounted = false;
    };
  }, [setAccount]);

  if (state === 'pending') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading text="登录态校验中..." />
      </div>
    );
  }

  if (state === 'guest') return <Navigate to="/portal/login" replace />;

  // portal 用户必须有 account 信息（fetchPortalProfile 成功就一定有），admin 用户允许为空
  if (state === 'portal' && !account) {
    return <Navigate to="/portal/login" replace />;
  }

  return <>{children}</>;
};

export default PortalGuard;
